/**
 * UserCreateModal — Admin form for creating a user directly (no invite email).
 * Spec: docs/superpowers/specs/2026-05-13-admin-create-user-design.md
 */
import { useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
  X, Mail, Shield, User as UserIcon, AlertCircle, Copy, CheckCircle, KeyRound, CreditCard,
} from 'lucide-react';
import type { UserRole, CreateUserRequest, CreateUserResponse } from '../../types/user';
import { listPlans } from '../../lib/api/admin-plans';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/Select';

// Radix Select rejects "" as an item value; use this sentinel for "no plan".
const PLAN_NONE = '__none__';

interface UserCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Performs the create call. Resolves with the response on success. */
  onCreate: (data: CreateUserRequest) => Promise<CreateUserResponse>;
}

type ModalState =
  | { kind: 'entry' }
  | { kind: 'success'; email: string; tempPassword: string };

export function UserCreateModal({ isOpen, onClose, onCreate }: UserCreateModalProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [planId, setPlanId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<ModalState>({ kind: 'entry' });
  const [copied, setCopied] = useState(false);

  // Fetch the list of plans when the modal is open. Admin-only endpoint
  // backed by the same /admin/plans query the PlansPage uses.
  const { data: plans } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: listPlans,
    enabled: isOpen,
  });
  const activePlans = (plans ?? []).filter((p) => p.is_active);

  const reset = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setRole('user');
    setPlanId('');
    setError(null);
    setIsSubmitting(false);
    setState({ kind: 'entry' });
    setCopied(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onCreate({
        email,
        role,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        plan_id: planId || undefined,
      });
      setState({ kind: 'success', email: result.user.email, tempPassword: result.temporary_password });
    } catch (err: unknown) {
      // Axios stringifies error.message as "Request failed with status code 409"
      // — pull the structured detail out of the response body instead.
      const detail = axios.isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      const status = axios.isAxiosError(err) ? err.response?.status : null;
      if (detail === 'user_already_exists' || status === 409) {
        setError('A user with that email already exists.');
      } else if (typeof detail === 'string' && detail.length > 0) {
        setError(detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to create user. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (state.kind !== 'success') return;
    try {
      await navigator.clipboard.writeText(state.tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be denied; user can select manually.
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-card bg-brand-bg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-brand-border px-6 py-4">
            <h3 className="text-lg font-semibold text-brand-text">
              {state.kind === 'entry' ? 'Create New User' : 'User Created'}
            </h3>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-brand-muted hover:text-brand-text disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {state.kind === 'entry' ? (
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                {error && (
                  <div className="rounded-input bg-brand-error/5 p-3 flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-brand-error flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-brand-error">{error}</div>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label htmlFor="create-email" className="block text-sm font-medium text-brand-text mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-brand-muted" />
                    </div>
                    <input
                      type="email"
                      id="create-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isSubmitting}
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-brand-surface disabled:text-brand-muted"
                      placeholder="user@example.com"
                    />
                  </div>
                </div>

                {/* First name */}
                <div>
                  <label htmlFor="create-first-name" className="block text-sm font-medium text-brand-text mb-1">
                    First Name <span className="text-brand-muted">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-brand-muted" />
                    </div>
                    <input
                      type="text"
                      id="create-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={isSubmitting}
                      className="block w-full pl-10 pr-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-brand-surface disabled:text-brand-muted"
                    />
                  </div>
                </div>

                {/* Last name */}
                <div>
                  <label htmlFor="create-last-name" className="block text-sm font-medium text-brand-text mb-1">
                    Last Name <span className="text-brand-muted">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="create-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={isSubmitting}
                    className="block w-full px-3 py-2 border border-brand-border rounded-input focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:bg-brand-surface disabled:text-brand-muted"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1">
                    Role
                  </label>
                  <Select
                    value={role}
                    onValueChange={(v) => setRole(v as UserRole)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-10 pl-10 relative">
                      <Shield className="h-5 w-5 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Subscription (optional) */}
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1">
                    Subscription <span className="text-brand-muted">(optional)</span>
                  </label>
                  <Select
                    value={planId || PLAN_NONE}
                    onValueChange={(v) => setPlanId(v === PLAN_NONE ? '' : v)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-10 pl-10 relative">
                      <CreditCard className="h-5 w-5 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PLAN_NONE}>No subscription</SelectItem>
                      {activePlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.display_name} ({plan.interval})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-brand-muted">
                    Admin-assigned plans bypass Stripe — the user is not charged.
                  </p>
                </div>

                <p className="text-xs text-brand-muted">
                  A one-time temporary password will be generated and shown after creation.
                  The user will be required to change it on first login.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-brand-border px-6 py-4 bg-brand-surface">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-brand-text bg-brand-bg border border-brand-border rounded-input hover:bg-brand-surface disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-brand-surface bg-brand-primary border border-transparent rounded-input hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-pill h-4 w-4 border-b-2 border-brand-surface" />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="px-6 py-4 space-y-4">
              <div className="rounded-input bg-green-50 p-3 flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-800">
                  Account created for <span className="font-medium">{state.email}</span>.
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">
                  Temporary Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-brand-muted" />
                  </div>
                  <input
                    type="text"
                    readOnly
                    value={state.tempPassword}
                    autoComplete="off"
                    spellCheck={false}
                    data-1p-ignore
                    data-lpignore="true"
                    onFocus={(e) => e.currentTarget.select()}
                    className="block w-full pl-10 pr-24 py-2 border border-brand-border rounded-input font-mono text-sm bg-brand-surface"
                  />
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-brand-primary hover:opacity-80"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="rounded-input bg-brand-error/5 p-3 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-brand-error flex-shrink-0 mt-0.5" />
                <div className="text-sm text-brand-error">
                  This password will not be shown again. Copy it now and share it with the user through a
                  secure channel. They will be required to change it on first login.
                </div>
              </div>

              <div className="flex items-center justify-end border-t border-brand-border pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-brand-surface bg-brand-primary rounded-input hover:opacity-90"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
